import { Controller } from "@hotwired/stimulus";
import Chart from "chart.js/auto";
import { getChartThemeColors, observeThemeChange } from "utils/chartTheme";
import { PLATFORM_COLORS } from "lib/platformColors.mjs";
import { abbreviateNumber } from "lib/abbreviateNumber.mjs";

// Channels page charts (channels/show).
//
// kind "spark": one channel's small trend line on its card, no axes or
// tooltip. series: [["2026-10-01", 123 | null], ...]; nulls are gaps.
//
// kind "weekly": one measure per week (blog pageviews or post views, never
// both), one stacked bar segment per channel.
// series: {weeks: ["2026-07-28", ...], unit: "views", series: [{name, provider, values}]}.
// A blog is drawn in --chart-1, a profile in its platform's colour.

function colorFor(provider) {
  if (provider === "blog") {
    return (
      getComputedStyle(document.documentElement)
        .getPropertyValue("--chart-1")
        .trim() || "#0A7EA4"
    );
  }
  return (
    PLATFORM_COLORS[provider] ||
    PLATFORM_COLORS[provider?.replace("_", "")] ||
    "#787A92"
  );
}

export default class extends Controller {
  static targets = ["canvas"];
  static values = {
    kind: String,
    series: Object,
    points: Array,
    provider: String,
  };

  connect() {
    this.draw();
    this.stopObserving = observeThemeChange(() => this.draw());
  }

  disconnect() {
    this.stopObserving?.();
    this.chart?.destroy();
  }

  draw() {
    this.chart?.destroy();
    const config = this.kindValue === "weekly" ? this.weekly() : this.spark();
    if (config) this.chart = new Chart(this.canvasTarget, config);
  }

  spark() {
    const points = this.pointsValue;
    if (!points.length) return null;
    const color = colorFor(this.providerValue);
    return {
      type: "line",
      data: {
        labels: points.map((p) => p[0]),
        datasets: [
          {
            data: points.map((p) => p[1]),
            borderColor: color,
            borderWidth: 2,
            pointRadius: 0,
            tension: 0.3,
            spanGaps: true,
            fill: false,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        plugins: { legend: { display: false }, tooltip: { enabled: false } },
        scales: { x: { display: false }, y: { display: false, grace: "10%" } },
      },
    };
  }

  weekly() {
    const { weeks, series, unit } = this.seriesValue;
    if (!weeks?.length || !series?.length) return null;
    const theme = getChartThemeColors();
    const fmt = (iso) =>
      new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      });
    return {
      type: "bar",
      data: {
        labels: weeks.map(fmt),
        datasets: series.map((s) => ({
          label: s.name,
          data: s.values,
          backgroundColor: colorFor(s.provider),
          borderRadius: 2,
          maxBarThickness: 28,
        })),
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: {
            position: "bottom",
            align: "start",
            labels: {
              color: theme.legendColor,
              boxWidth: 10,
              boxHeight: 10,
              usePointStyle: true,
              pointStyle: "rectRounded",
            },
          },
          tooltip: {
            backgroundColor: theme.tooltipBackgroundColor,
            titleColor: theme.tooltipTitleColor,
            bodyColor: theme.tooltipBodyColor,
            borderColor: theme.tooltipBorderColor,
            borderWidth: 1,
            callbacks: {
              title: (items) => `Week of ${items[0].label}`,
              label: (item) =>
                `${item.dataset.label}: ${item.formattedValue} ${
                  unit || ""
                }`.trim(),
            },
          },
        },
        scales: {
          x: {
            stacked: true,
            grid: { display: false },
            ticks: {
              color: theme.tickColor,
              maxRotation: 0,
              autoSkipPadding: 12,
            },
          },
          y: {
            stacked: true,
            beginAtZero: true,
            grid: { color: theme.gridColor },
            ticks: {
              color: theme.tickColor,
              callback: (v) => abbreviateNumber(v, 1000, 1),
            },
          },
        },
      },
    };
  }
}
